import { X } from "lucide-react";
import { DiffViewer } from "../DiffViewer";

export interface VersionModalField {
  label: string;
  type: "text" | "select" | "checkbox";
  value: any;
  onChange: (val: any) => void;
  options?: { value: string | number; label: string }[];
  placeholder?: string;
  description?: string;
}

interface UnifiedRevisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  fields?: VersionModalField[];
  contentLabel: string;
  contentValue: string;
  onChangeContent: (val: string) => void;
  contentRows?: number;
  oldTitle: string;
  newTitle: string;
  oldContent: string;
  newContent: string;
  confirmLabel: string;
  onConfirm: () => void;
  submitting: boolean;
}

export function UnifiedRevisionModal({
  isOpen,
  onClose,
  title,
  subtitle,
  fields = [],
  contentLabel,
  contentValue,
  onChangeContent,
  contentRows = 6,
  oldTitle,
  newTitle,
  oldContent,
  newContent,
  confirmLabel,
  onConfirm,
  submitting,
}: UnifiedRevisionModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 dark:bg-black/70 backdrop-blur-xs">
      <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 shadow-xl max-w-4xl w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-stone-100 dark:border-stone-800 pb-3">
          <div>
            <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">{title}</h3>
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 p-1 rounded-md"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {fields.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {fields.map((f, idx) => (
              <div key={idx} className="space-y-1">
                <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">
                  {f.label}
                </label>
                {f.type === "checkbox" ? (
                  <label className="flex items-center gap-2 cursor-pointer mt-1">
                    <input
                      type="checkbox"
                      checked={Boolean(f.value)}
                      onChange={(e) => f.onChange(e.target.checked)}
                      className="rounded border-stone-300 dark:border-stone-700 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-xs text-stone-700 dark:text-stone-300 font-medium">
                      {f.description || f.label}
                    </span>
                  </label>
                ) : f.type === "select" ? (
                  <select
                    value={f.value}
                    onChange={(e) => f.onChange(e.target.value)}
                    className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
                  >
                    {f.options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={f.value}
                    placeholder={f.placeholder}
                    onChange={(e) => f.onChange(e.target.value)}
                    className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">
              {contentLabel}
            </label>
            <span className="text-[11px] text-stone-400 dark:text-stone-500 font-mono">
              {contentValue.length} characters
            </span>
          </div>
          <textarea
            rows={contentRows}
            value={contentValue}
            onChange={(e) => onChangeContent(e.target.value)}
            className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-3 font-mono text-xs text-stone-900 dark:text-stone-100 leading-relaxed focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
          />
        </div>

        <div className="space-y-2 pt-2 border-t border-stone-100 dark:border-stone-800">
          <div className="text-xs font-semibold text-stone-700 dark:text-stone-300">
            Real-Time Diff Preview
          </div>
          <div className="max-h-64 overflow-y-auto border border-stone-200 dark:border-stone-800 rounded-lg">
            <DiffViewer
              oldTitle={oldTitle}
              newTitle={newTitle}
              oldText={oldContent}
              newText={newContent}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-stone-100 dark:border-stone-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-xs font-medium text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white disabled:opacity-50 cursor-pointer"
          >
            {submitting ? "Saving..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
